import type { ExtensionRevision, LifecycleCommand } from "@/lib/api/courseworkPolicy";

type Action = LifecycleCommand["action"];

/** Each lifecycle action in the words a teacher means by it, from choice to receipt. */
export const LIFECYCLE: Record<Action, { option: string; verb: string; does: string; done: string; event: string }> = {
  close: { option: "Close to new hand-ins", verb: "Close hand-ins", does: "Stop new hand-ins and record your reason.", done: "Closed to new hand-ins. Attempts, marks and released feedback are kept.", event: "Closed to new hand-ins" },
  reopen: { option: "Reopen for hand-ins", verb: "Reopen hand-ins", does: "Accept hand-ins again and record your reason.", done: "Reopened for hand-ins. Extensions ended by the close stay ended.", event: "Reopened for hand-ins" },
  archive: { option: "Archive", verb: "Archive coursework", does: "Move this coursework to the archive and record your reason.", done: "Archived. Outstanding marking and release stay in their queues, and learners keep released marks and feedback.", event: "Archived" },
  restore: { option: "Restore", verb: "Restore coursework", does: "Return this coursework to active coursework and record your reason.", done: "Restored to active coursework. Attempts, marks and feedback are unchanged.", event: "Restored" },
};

/** The task's name says which actions the coursework's state offers. */
export function lifecycleLabel(state: { archived: boolean; closed: boolean }): string {
  return state.archived ? "Restore coursework" : state.closed ? "Reopen or archive" : "Close or archive";
}

/** A recorded event's verb: the action a person took, never the stored word. */
export const eventWords = (action: string) => (LIFECYCLE as Record<string, { event: string } | undefined>)[action]?.event ?? action;

type Words = { name: (enrollmentId: string) => string; actor: (userId: string) => string | null; at: (instant: string) => string };

/** One learner's extension record, oldest first, as the teaching team reads it. */
export function extensionTrail(rows: ExtensionRevision[], words: Pick<Words, "actor" | "at">): string[] {
  return rows.map(row => {
    const by = [words.actor(row.actor_id), words.at(row.created_at)].filter(Boolean).join(", ");
    return row.state === "approved"
      ? `Extended to ${words.at(row.due_at)}${by ? ` by ${by}` : ""}. Reason: ${row.reason}`
      : `Extension to ${words.at(row.due_at)} ended${by ? ` by ${by}` : ""} when the work was closed or archived. Reason: ${row.reason}`;
  });
}

/**
 * Where a learner's deadline stands now. An ended extension says so, with the
 * deadline the learner has instead, because reopening never restores it.
 */
export function extensionStanding(latest: ExtensionRevision, active: boolean, baseDue: string | null, words: Pick<Words, "name" | "at">): string {
  const name = words.name(latest.enrollment_id);
  if (latest.state === "ended") return `${name}'s extension to ${words.at(latest.due_at)} ended at ${words.at(latest.created_at)}. Reopening doesn't restore it.${baseDue ? ` Their deadline is now the coursework's own: ${words.at(baseDue)}.` : ""}`;
  return active ? `${name}'s deadline is ${words.at(latest.due_at)}, by extension.` : `${name}'s extended deadline of ${words.at(latest.due_at)} has passed.`;
}

/** A learner's current deadline: their approved extension, else the coursework's own. */
export function currentDeadline(latest: ExtensionRevision | undefined, baseDue: string | null): string | null {
  return latest?.state === "approved" ? latest.due_at : baseDue;
}
